import { useId, forwardRef, useMemo } from "react"
import Select from "react-select"
import AsyncSelect from "react-select/async"
import { Layout } from "./layout.js"
import { classes, belowViewPort } from "utils"
import { typography, colors } from "design"
import { useFormContext, Controller } from "react-hook-form"

export const dropdownVariablesLight = {
  "--resbird-dropdown-focus-border-color": colors.primaryBlueGrey,
  "--resbird-dropdown-border-color": colors.grey400,
  "--resbird-dropdown-color": colors.black,
  "--resbird-dropdown-color-error": colors.red,
  "--resbird-dropdown-label-color": colors.lightGrey100,
  "--resbird-dropdown-label-error": colors.red,
  "--resbird-dropdown-error-border-color": colors.red,
  "--resbird-dropdown-required-color": colors.red,
  "--resbird-dropdown-disabled-background": colors.grey200,
  "--resbird-dropdown-selected-option-background": colors.primaryBlueGrey,
  "--resbird-dropdown-option-hover-background": colors.lightBlueGrey,
  "--resbird-dropdown-option-background": colors.cream,
  "--resbird-dropdown-option-color": colors.grey400,
  "--resbird-dropdown-option-selected-color": colors.lightBlueGrey,
}

const computeDropdownStyle = ({ error, isDisabled, ...props }) => ({
  width: "100%",
  background: "white",
  borderRadius: 5,
  control: (baseStyles, { isFocused }) => ({
    ...baseStyles,
    cursor: "pointer",
    height: 45,
    width: "inherit",
    borderRadius: 5,
    fontSize: "1rem",
    boxShadow: "none",
    borderWidth: isFocused ? 2 : 1,
    ...belowViewPort(1441, { fontSize: "0.75rem" }),
    backgroundColor: isDisabled
      ? "var(--resbird-dropdown-disabled-background)"
      : "inherit",
    borderColor: error
      ? "var(--resbird-dropdown-error-border-color)"
      : isFocused
        ? "var(--resbird-dropdown-focus-border-color)"
        : "var(--resbird-dropdown-border-color)",
    ":hover": {
      borderColor: error
        ? "var(--resbird-dropdown-error-border-color)"
        : isFocused
          ? "var(--resbird-dropdown-focus-border-color)"
          : "var(--resbird-dropdown-border-color)",
    },
    ...props,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "var(--resbird-dropdown-color)", // Change the font color of the selected value
    fontWeight: 400,
    lineHeight: "24px",
    fontSize: "1rem",
    ...belowViewPort(1441, { fontSize: "0.75rem" }),
  }),
  option: (provided, { isSelected, isFocused }) => ({
    ...provided,
    backgroundColor: isSelected
      ? "var(--resbird-dropdown-selected-option-background)"
      : isFocused
        ? "var(--resbird-dropdown-option-hover-background)"
        : "var(--resbird-dropdown-option-background)",
    color: isSelected
      ? "var(--resbird-dropdown-option-selected-color)"
      : "var(--resbird-dropdown-option-color)",
    padding: "8px",
    fontSize: "1rem",
    fontWeight: 400,
    cursor: "pointer",
    ...belowViewPort(1441, { fontSize: "0.75rem" }),
    "&:hover": {
      backgroundColor: isSelected
        ? "var(--resbird-dropdown-selected-option-background)"
        : "var(--resbird-dropdown-option-hover-background)",
    },
  }),
  indicatorSeparator: () => ({
    display: "none", // This removes the vertical line
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 200,
  }),
})

export const Dropdown = forwardRef(
  (
    {
      error: propsError,
      label,
      fieldName,
      rules = {},
      loadOptions,
      className,
      labelStyle,
      errorStyle,
      disabled: isDisabled,
      dropdownStyle: propsDropdownStyle = {},
      placeHolder,
      onChange,
      options,
      ...props
    },
    propsRef,
  ) => {
    const id = useId()
    const errorId = useId()
    const { getValues } = useFormContext()

    const { formState, control } = useFormContext()

    const error = useMemo(
      () =>
        (!_.isEmpty(rules) || props.required) &&
        (propsError || _.get(formState.errors, fieldName)?.message),
      [fieldName, formState, props.required, propsError, rules],
    )

    const dropdownStyle = useMemo(
      () => computeDropdownStyle({ error, isDisabled, ...propsDropdownStyle }),
      [error, isDisabled, propsDropdownStyle],
    )

    const defaultValueLabel = useMemo(
      () => _.find(options, { value: getValues(fieldName) })?.label,
      [fieldName, getValues, options],
    )

    const SelectComponent = loadOptions ? AsyncSelect : Select

    return (
      <Controller
        control={control}
        name={fieldName}
        ref={propsRef}
        rules={rules}
        render={({
          field: { onChange: controllerOnChange, onBlur, value, ref },
        }) => (
          <Layout
            className={classes("dropdown-container", className)}
            css={{ cursor: "pointer" }}
            role="select"
            vertical
          >
            {label && (
              <label
                className="select-label"
                css={[
                  {
                    marginBottom: 11,
                    color: "var(--resbird-dropdown-label-color)",
                  },
                  typography.widgetLabel,
                  labelStyle,
                ]}
                htmlFor={id}
              >
                <span>{`${label}:`}</span>
                {rules.required && (
                  <span
                    className="required-indicator"
                    css={{ color: "var(--resbird-dropdown-required-color)" }}
                  >
                    *
                  </span>
                )}
              </label>
            )}
            <SelectComponent
              className="select"
              menuPlacement="auto"
              options={options}
              loadOptions={loadOptions}
              styles={dropdownStyle}
              aria-describedby={error ? errorId : undefined}
              aria-labelledby={id}
              css={[dropdownStyle, propsDropdownStyle]}
              placeholder={defaultValueLabel || placeHolder || "Select"}
              inputId={id}
              isDisabled={isDisabled}
              noOptionsMessage={() => t("widgets.dropdown.no_options")}
              menuPortalTarget={document.body}
              onChange={({ value }) => {
                if (isDisabled) return
                controllerOnChange(value)
                onChange?.(value)
              }}
              value={value ? _.find(options, (c) => c.value === value) : null}
              ref={ref}
              tabSelectsValue
              {...{ onBlur, ...props }}
            />
            {error && (
              <label
                className="error-description"
                css={[
                  errorStyle,
                  {
                    marginTop: 5,
                    fontSize: 14,
                    color: "var(--resbird-dropdown-label-error)",
                  },
                ]}
                role="aria-errormessage"
                htmlFor={id}
              >
                {error}
              </label>
            )}
          </Layout>
        )}
      />
    )
  },
)

Dropdown.displayName = "Dropdown"
